import React, { useState } from 'react';

import { STD_RANGES, daysAt, fmtDays, rangeIndexForTrade, stdIndexForTradeColumn } from './ranges';
import { linkedPresetForCard, activePresetForUi, exactMatch, suggestedPresetForUi } from './resolve';
import { TatStyles as S } from './styles';

const TurnaroundBlock = ({
  cfg,
  resolved,
  picks,
  chosenPresetId,
  typicalStdIdx,
  onTypicalRange,
  onSwitchCard,
  askTypicalRange,
  readOnly,
  blockRef
}) => {
  const [compareOpen, setCompareOpen] = useState(false);

  if (!cfg?.cards?.length) return null;
  if (!resolved && cfg.singleCard && !cfg.presets.length) return null;

  const colIdx = rangeIndexForTrade(cfg, typicalStdIdx);
  const hasRange = typicalStdIdx != null;
  const card = resolved?.card;
  const preset = resolved?.preset;
  const uiActive = activePresetForUi(cfg, picks, chosenPresetId, preset);
  const suggested = suggestedPresetForUi(cfg, picks, chosenPresetId);
  const headDays = daysAt(card, colIdx, { side: 'customer' });

  const others =
    card && !cfg.singleCard
      ? cfg.cards
          .filter(c => c.id !== card.id)
          .map(c => {
            const target = linkedPresetForCard(cfg, c.id);
            const mine = daysAt(card, colIdx, { side: 'customer' });
            const theirs = daysAt(c, colIdx, { side: 'customer' });
            const heavier = c.tier > card.tier;
            const names = heavier
              ? [...(target?.addsItems || []), ...(target?.addsAnswers || [])]
              : [...(preset?.addsItems || []), ...(preset?.addsAnswers || [])];
            const shown = names.slice(0, 3).join(', ') + (names.length > 3 ? ` and ${names.length - 3} more` : '');
            let delta = '—';
            let deltaTone = 'muted';
            if (hasRange && mine != null && theirs != null) {
              const d = theirs - mine;
              delta = d === 0 ? 'same days' : `${d > 0 ? '+' : '−'}${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'}`;
              deltaTone = d > 0 ? 'warn' : d < 0 ? 'good' : 'neutral';
            } else if (hasRange && theirs == null) {
              delta = 'Quote';
            }
            return {
              card: c,
              target,
              changeLine: shown ? `${heavier ? 'Adds' : 'Drops'} ${shown}` : heavier ? 'Heavier card' : 'Lighter card',
              delta,
              deltaTone,
              deltaSub: hasRange ? `at ${cfg.ranges[colIdx]} sheets` : 'pick a typical range'
            };
          })
      : [];

  // Same TAT card, multiple presets — Switch between presets (not cards).
  const otherPresetsOnCard =
    card && (cfg.presets || []).length > 1
      ? cfg.presets.filter(
          p => String(p.cardId) === String(card.id) && (!uiActive || String(p.id) !== String(uiActive.id))
        )
      : [];

  const tierLine = card
    ? cfg.singleCard
      ? `the only card for this trade`
      : `card ${card.tier} of ${card.of} for this trade`
    : '';

  const applicable = suggested;
  let presetLine = '';
  if (applicable) {
    presetLine = `TAT card applicable: ${applicable.name}`;
  } else if (uiActive) {
    if (exactMatch(cfg, uiActive, picks)) {
      presetLine = `Preset: ${uiActive.name}`;
    } else if (resolved && !resolved.exact && String(resolved.preset?.id) === String(uiActive.id)) {
      presetLine = `Preset: customised from ${uiActive.name}`;
    } else {
      presetLine = `Preset: ${uiActive.name}`;
    }
  }

  const why = resolved?.why || [];

  return (
    <S.TatCard ref={blockRef} id={`tat-${cfg.id}`}>
      <S.TatIntro>Scope changes map to a turnaround card — business days by sheet range.</S.TatIntro>

      {askTypicalRange && (
        <S.TypicalAsk>
          <S.TypicalAskLabel>What’s a typical sheet count for jobs like this?</S.TypicalAskLabel>
          <S.TypicalAskHint>Asked once — used to highlight days on every trade’s card.</S.TypicalAskHint>
          <S.ChipRow role='radiogroup' aria-label='Typical sheet range'>
            {STD_RANGES.map((label, i) => (
              <S.RangeChip
                key={label}
                type='button'
                role='radio'
                aria-checked={typicalStdIdx === i}
                $on={typicalStdIdx === i}
                disabled={readOnly}
                onClick={() => !readOnly && onTypicalRange && onTypicalRange(i)}>
                {label}
              </S.RangeChip>
            ))}
          </S.ChipRow>
        </S.TypicalAsk>
      )}

      {card ? (
        <>
          <S.TatHeadRow>
            <S.TatHeadLeft>
              <S.TatCardName>Card: {card.name}</S.TatCardName>
              <S.TatTier>{tierLine}</S.TatTier>
            </S.TatHeadLeft>
            <S.TatHeadRight>
              <S.TatHeadline $ready={hasRange && headDays != null}>
                {hasRange ? (headDays == null ? 'Quote' : fmtDays(headDays)) : 'Pick a typical range'}
              </S.TatHeadline>
              <S.TatHeadSub>
                {hasRange ? `at ${cfg.ranges[colIdx]} sheets` : 'to see business days for this card'}
              </S.TatHeadSub>
            </S.TatHeadRight>
          </S.TatHeadRow>

          {presetLine ? <S.TatPresetLine>{presetLine}</S.TatPresetLine> : null}
          {why.length > 0 ? (
            <S.TatWhy>
              <S.TatWhyLabel>Why this card:</S.TatWhyLabel> {why.join(', ')}
            </S.TatWhy>
          ) : null}

          <S.RangeGrid
            role='radiogroup'
            aria-label='Business days by sheet range'
            style={{ gridTemplateColumns: `repeat(${cfg.ranges.length}, minmax(0, 1fr))` }}>
            {cfg.ranges.map((lab, i) => {
              const d = daysAt(card, i, { side: 'customer' });
              const hi = hasRange && i === colIdx;
              const std = stdIndexForTradeColumn(cfg, i);
              return (
                <S.RangeCell
                  key={lab}
                  type='button'
                  $hi={hi}
                  disabled={readOnly || std == null}
                  title={`${lab} sheets · ${d == null ? 'quoted' : `${d} business days`}`}
                  onClick={() => {
                    if (readOnly || std == null || !onTypicalRange) return;
                    onTypicalRange(std);
                  }}>
                  <S.RangeCellHead $hi={hi}>{lab}</S.RangeCellHead>
                  <S.RangeCellVal $hi={hi}>{d == null ? 'Quote' : d}</S.RangeCellVal>
                  <S.RangeCellUnit $hi={hi}>{d == null ? 'on acceptance' : d === 1 ? 'day' : 'days'}</S.RangeCellUnit>
                </S.RangeCell>
              );
            })}
          </S.RangeGrid>

          {cfg.customRanges ? (
            <S.TatNote>
              This trade uses its own sheet ranges. Your typical range maps to the column that contains it.
            </S.TatNote>
          ) : null}

          {cfg.singleCard ? (
            <S.TatNote>
              Scope changes what Beam measures on this trade, not the turnaround — there’s only one card.
              {otherPresetsOnCard.length > 0 ? ' You can still switch between presets on this card.' : ''}
            </S.TatNote>
          ) : null}

          {otherPresetsOnCard.length > 0 ? (
            <S.OthersBlock>
              <S.OthersHead>
                <S.OthersTitle>
                  {cfg.singleCard ? 'Other presets on this card' : 'Other presets on your card'}
                </S.OthersTitle>
              </S.OthersHead>
              {otherPresetsOnCard.map(p => {
                const adds = [...(p.addsItems || []), ...(p.addsAnswers || [])];
                const shown = adds.slice(0, 3).join(', ') + (adds.length > 3 ? ` and ${adds.length - 3} more` : '');
                return (
                  <S.OtherRow key={p.id}>
                    <S.OtherMain>
                      <S.OtherName>{p.name}</S.OtherName>
                      <S.OtherChange>
                        {shown ? `Adds ${shown}` : p.desc || 'Same turnaround card, different scope'}
                      </S.OtherChange>
                    </S.OtherMain>
                    <S.OtherDelta $tone='muted'>
                      <strong>same days</strong>
                      <span>same card</span>
                    </S.OtherDelta>
                    {!readOnly ? (
                      <S.SwitchBtn type='button' onClick={() => onSwitchCard && onSwitchCard(p)}>
                        Switch
                      </S.SwitchBtn>
                    ) : null}
                  </S.OtherRow>
                );
              })}
            </S.OthersBlock>
          ) : null}

          {others.length > 0 ? (
            <S.OthersBlock>
              <S.OthersHead>
                <S.OthersTitle>Other cards for this trade</S.OthersTitle>
                <S.CompareBtn type='button' onClick={() => setCompareOpen(o => !o)}>
                  {compareOpen ? 'Hide comparison' : 'Compare all cards'}
                </S.CompareBtn>
              </S.OthersHead>
              {others.map(o => (
                <S.OtherRow key={o.card.id}>
                  <S.OtherMain>
                    <S.OtherName>
                      {o.card.name} · card {o.card.tier} of {o.card.of}
                    </S.OtherName>
                    <S.OtherChange>{o.changeLine}</S.OtherChange>
                  </S.OtherMain>
                  <S.OtherDelta $tone={o.deltaTone}>
                    <strong>{o.delta}</strong>
                    <span>{o.deltaSub}</span>
                  </S.OtherDelta>
                  {!readOnly && o.target ? (
                    <S.SwitchBtn type='button' onClick={() => onSwitchCard && onSwitchCard(o.target)}>
                      Switch
                    </S.SwitchBtn>
                  ) : null}
                </S.OtherRow>
              ))}

              {compareOpen ? (
                <S.CompareTable>
                  <S.CompareHead
                    style={{
                      gridTemplateColumns: `200px repeat(${cfg.ranges.length}, minmax(0, 1fr))`
                    }}>
                    <span>Card</span>
                    {cfg.ranges.map((lab, i) => (
                      <S.CompareColHead key={lab} $hi={hasRange && i === colIdx}>
                        {lab}
                      </S.CompareColHead>
                    ))}
                  </S.CompareHead>
                  {cfg.cards.map(c => {
                    const isResolved = c.id === card.id;
                    return (
                      <S.CompareRow
                        key={c.id}
                        $on={isResolved}
                        style={{
                          gridTemplateColumns: `200px repeat(${cfg.ranges.length}, minmax(0, 1fr))`
                        }}>
                        <S.CompareName>
                          {c.name}
                          {isResolved ? ' · yours' : ''}
                        </S.CompareName>
                        {cfg.ranges.map((lab, i) => {
                          const d = daysAt(c, i, { side: 'customer' });
                          return (
                            <S.CompareCell key={lab} $hi={hasRange && i === colIdx}>
                              {d == null ? 'Quote' : d}
                            </S.CompareCell>
                          );
                        })}
                      </S.CompareRow>
                    );
                  })}
                </S.CompareTable>
              ) : null}
            </S.OthersBlock>
          ) : null}
        </>
      ) : (
        <S.TatNote>Tick items or choose a preset to see which turnaround card applies.</S.TatNote>
      )}
    </S.TatCard>
  );
};

export default TurnaroundBlock;
